import { calculateAccommodationNights } from "@/lib/itineraryCostUtils";

export interface SupplementSourceItem {
  id: string;
  title?: string | null;
  supplier?: string | null;
  item_type?: string | null;
  accommodation_type?: string | null;
  occupancy_type?: string | null;
  room_guest_type?: string | null;
  room_rate_basis?: string | null;
  cost?: number | null;
  check_in?: string | null;
  check_out?: string | null;
  include_in_quote?: boolean | null;
}

export interface SupplementLine {
  itemId: string;
  label: string;
  nights: number;
  /** Net supplement per night, before markup */
  perNight: number;
  /** Percentage of the room rate used for this line */
  percent: number;
  /** Net line total, before markup */
  total: number;
}

const OCCUPANCY_DIVISORS: Record<string, number> = {
  single: 1,
  double: 2,
  twin: 2,
  triple: 3,
  quad: 4,
};

/**
 * Accommodation rows that a single supplement can apply to:
 * guest rooms, included in the quote, priced, with a real stay window,
 * and not already booked as single occupancy. Villas / private hire are
 * excluded — they are not sold per person.
 */
export function isSupplementEligible(item: SupplementSourceItem): boolean {
  if (item.item_type !== "accommodation") return false;
  if (item.include_in_quote === false) return false;
  if (item.room_guest_type === "staff") return false;
  if ((item.accommodation_type || "").toLowerCase() === "villas") return false;
  if ((item.occupancy_type || "").toLowerCase() === "single") return false;
  if (!item.check_in || !item.check_out) return false;
  if (!(item.cost && item.cost > 0)) return false;
  return true;
}

/**
 * Builds one supplement line per eligible accommodation stay.
 * The supplement is a straight percentage (default 50%) of the room rate
 * entered on the itinerary line, per night. Per-person rate bases are
 * normalised to a full room rate first.
 */
export function buildSingleSupplementLines(
  items: SupplementSourceItem[],
  options?: { percent?: number }
): SupplementLine[] {
  const percent = options?.percent ?? 50;

  return (items || []).filter(isSupplementEligible).map((item) => {
    const nights = Math.max(calculateAccommodationNights(item.check_in, item.check_out), 1);
    const divisor = OCCUPANCY_DIVISORS[(item.occupancy_type || "double").toLowerCase()] || 2;
    const enteredCost = item.cost || 0;

    // Normalise the entered cost to a full room rate when it was entered per person.
    const isPerPerson = (item.room_rate_basis || "per_room") === "per_person";
    const roomRateValue = isPerPerson ? enteredCost * divisor : enteredCost;

    const perNight = Math.round(roomRateValue * (percent / 100) * 100) / 100;

    return {
      itemId: item.id,
      label: item.title || item.supplier || "Accommodation",
      nights,
      perNight,
      percent,
      total: Math.round(perNight * nights * 100) / 100,
    };
  });
}

/** Net total across the supplement lines, before markup. */
export function sumSupplementLines(lines: SupplementLine[]): number {
  return Math.round(lines.reduce((sum, l) => sum + (l.total || 0), 0) * 100) / 100;
}

/** Applies the quote markup, matching the costings sell-price formula. */
export function applySupplementMarkup(netTotal: number, markupPercent: number): number {
  const marked = netTotal * (1 + (markupPercent || 0) / 100);
  return Math.round(marked * 100) / 100;
}
